import { addDays } from 'date-fns';
import { combineLatest, from, of } from 'rxjs';
import { catchError, filter, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { createSaveTableSettingsEpic } from '../../../common/datagrid';
import { PriceClient } from '../../../common/PricePrgClient';
import { notifyPricePlansUpdated } from '../../../common/signalr/signalrActions';
import { RootEpic } from '../../../store';
import { format, createEpicFromAsyncAction } from '../../../utils';
import { fetchPricelists } from '../../pricelist/redux/pricelistSlices';
import { fetchUserSettings } from '../../settings/redux/settingsSlices';
import { fetchStocksForDay } from '../../stock/redux/stockSlices';
import { enqueueSnackbar, showConfirmDialog } from '../../ui/redux/uiSlices';
import { dayAmount } from '../basePriceTypes';
import {
    basePriceTableActions,
    cancelPricePlanUpdate,
    fetchPricePlans,
    pricelistSelected,
    updateDefaultBasePricelistRequest,
    updateDefaultBasePricelistSuccess,
    updatePricePlan,
    warehouseDateSelected
} from './basePriceSlices';

const priceClient = new PriceClient();

const fetchPricePlansWhenPricelistSelected$: RootEpic = action$ =>
    action$.pipe(
        filter(pricelistSelected.match),
        mergeMap(action =>
            of(
                fetchPricePlans.request({
                    pricelistId: action.payload,
                    dayAmount: dayAmount
                }),
                updateDefaultBasePricelistRequest(action.payload)
            )
        )
    );

const fetchPricePlansWhenDefaultPricelistSelected$: RootEpic = action$ =>
    // Wait until both user settings and pricelists has been fetched before fetching price plans
    combineLatest([
        action$.pipe(filter(fetchUserSettings.success.match)),
        action$.pipe(filter(fetchPricelists.success.match))
    ]).pipe(
        filter(([userSettingsAction, pricelistsAction]) => {
            if (userSettingsAction?.payload == null) {
                return false;
            }

            const userSettings = userSettingsAction.payload;
            const pricelists = pricelistsAction.payload;

            return (
                (userSettings.selectedBasePricelistId ?? -1) > 0 &&
                pricelists.find(pl => pl.pricelistId === userSettings.selectedBasePricelistId)
                    ?.isBasePricelist === true
            );
        }),
        map(([a]) =>
            fetchPricePlans.request({
                pricelistId: a.payload.selectedBasePricelistId!,
                dayAmount: dayAmount
            })
        )
    );

const fetchPricePlans$ = createEpicFromAsyncAction(fetchPricePlans, payload =>
    priceClient.getPricePlans(payload.pricelistId, payload.dayAmount)
);

const updatePricePlan$: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(updatePricePlan.request.match),
        withLatestFrom(state$.pipe(map(state => state.basePrice.ui.selectedPricelistId))),
        filter(([, plId]) => plId != null),
        mergeMap(([action, pricelistId]) => {
            if (action.payload.dayIndex === 0 && action.payload.isAwareOfChangingActualPricelist !== true) {
                return of(
                    showConfirmDialog({
                        dialogTitle: 'Changing actual pricelist',
                        dialogMessage:
                            'You are about to change a price on the actual pricelist. Are you sure you want do to that?',
                        primaryActionName: 'Save',
                        okAction: updatePricePlan.request({
                            ...action.payload,
                            isAwareOfChangingActualPricelist: true
                        }),
                        cancelAction: cancelPricePlanUpdate({
                            dayIndex: action.payload.dayIndex,
                            productId: action.payload.productId
                        })
                    })
                );
            }

            return from(
                priceClient.updatePricePlan({
                    pricelistId: pricelistId!,
                    dayIndex: action.payload.dayIndex,
                    price: action.payload.newPrice,
                    productId: action.payload.productId,
                    awareOfCreatingNewPricelistPeriods:
                        action.payload.isAwareOfCreatingNewPricelistPeriods === true
                })
            ).pipe(
                mergeMap(response => {
                    const { payload } = action;
                    if (response?.message != null) {
                        return of(
                            showConfirmDialog({
                                dialogTitle: 'New pricelist period will be created',
                                dialogMessage: `Pricelist period is missing for ${format(
                                    addDays(new Date(), action.payload.dayIndex + 1),
                                    'eee dd/MM'
                                )}. By clicking create it will automatically be created.`,
                                primaryActionName: 'Create pricelist period',
                                okAction: updatePricePlan.request({
                                    ...payload,
                                    isAwareOfCreatingNewPricelistPeriods: true
                                }),
                                cancelAction: cancelPricePlanUpdate({
                                    dayIndex: action.payload.dayIndex,
                                    productId: action.payload.productId
                                })
                            })
                        );
                    }

                    return of(
                        notifyPricePlansUpdated({
                            pricelistId: pricelistId!,
                            productId: action.payload.productId
                        })
                    );
                }),
                catchError(() =>
                    of(
                        enqueueSnackbar({
                            message: 'Failed to update price',
                            severity: 'error',
                            autoHideDuration: 15000
                        }),
                        cancelPricePlanUpdate({
                            dayIndex: action.payload.dayIndex,
                            productId: action.payload.productId
                        })
                    )
                )
            );
        })
    );

const fetchWarehouseBalance$: RootEpic = action$ =>
    action$.pipe(
        filter(warehouseDateSelected.match),
        filter(({ payload }) => payload.date != null && payload.warehouse != null),
        map(({ payload }) =>
            fetchStocksForDay.request({ date: payload.date, warehouseId: payload.warehouse })
        )
    );

const savePriceTable$ = createSaveTableSettingsEpic(
    basePriceTableActions,
    state => state.basePrice.ui.basePriceTable
);

const updateDefaultSelectedBasePricelist$ = createEpicFromAsyncAction(
    {
        request: updateDefaultBasePricelistRequest,
        success: updateDefaultBasePricelistSuccess
    },
    (request, { userSettingsClient }) => userSettingsClient.setDefaultSelectedBasePricelist(request)
);

export default [
    fetchPricePlans$,
    fetchPricePlansWhenPricelistSelected$,
    updatePricePlan$,
    fetchWarehouseBalance$,
    savePriceTable$,
    updateDefaultSelectedBasePricelist$,
    fetchPricePlansWhenDefaultPricelistSelected$
];
